import {Alert, useTheme} from '@mui/material';
import {Box} from '@mui/system';
import dayjs from 'dayjs';
import _ from 'lodash';
import {useEffect, useMemo, useState} from 'react';
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {MAX_BAR_SIZE} from '../../../constants/common';
import {
  MachineInteractionBarItem,
  MachineInteractionBarQuery,
  MachineInteractionBarResponse,
} from '../../../interfaces/MachineInteraction';
import {usePanelFilter} from '../../dashboards/entities/DashboardEntityContext';
import {MachineInteractionReportData} from './data';

interface Props {
  value?: MachineInteractionReportData;
  refreshTrigger?: number;
}

const MachineInteractionReportBar = ({value, refreshTrigger}: Props) => {
  const {filter: config} = usePanelFilter();

  /*********/
  /* fetch */
  /*********/
  const [fetchedData, setFetchedData] = useState<MachineInteractionBarItem[]>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const fetchData = async (params: MachineInteractionBarQuery) => {
    if (!params.external_id) {
      return;
    }

    setFetchedInProgress(true);
    setFetchedErrors([]);
    try {
      const resp = await API.get<MachineInteractionBarResponse>(
        `${apiBaseUrl}/report/machine-interaction-chart`,
        {params}
      );
      setFetchedData(resp.data?.results || []);
      setIsFirstLoad(false);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, "STRA_23104350");
      setFetchedErrors(messages);
    }
    setFetchedInProgress(false);
  };

  const params = useMemo<MachineInteractionBarQuery>(
    () => ({
      date_start:
        config.params?.date_start ??
        value?.params?.date_start ??
        dayjs().format('YYYY-MM-DD'),
      date_end:
        config.params?.date_end ??
        value?.params?.date_end ??
        dayjs().format('YYYY-MM-DD'),
      external_id: config.params?.machines?.[0],
      humans: config.params?.humans ?? value?.params?.humans ?? [],
      zone_id: config.params && 'section_id' in config.params ? config.params.section_id ?? null : (value?.params?.section_id ?? null),
      input: JSON.stringify(config.params?.input ?? value?.params?.input),
      exclude_miners:
        config.params?.exclude_miners ?? value?.params?.exclude_miners ?? 1,
    }),
    [config]
  );

  useEffect(() => {
    if (params) {
      if (config.autoReload !== false || isFirstLoad) {
        fetchData(params);
      }
    }
  }, [params]);

  useEffect(() => {
    if (refreshTrigger && params) {
      fetchData(params);
    }
  }, [refreshTrigger]);

  /*********/
  /* chart */
  /*********/
  const [hiddenKeys, setHiddenKeys] = useState<string[]>([]);
  const toggleKey = (key: string) => {
    setHiddenKeys(
      hiddenKeys.includes(key)
        ? hiddenKeys.filter((i) => i !== key)
        : [...hiddenKeys, key]
    );
  };
  const theme = useTheme();

  const dataset = useMemo(() => {
    return _.map(
      fetchedData,
      ({hazard_total, remote_total, warning_total, period, date}) => ({
        period,
        date,
        hazards: hazard_total,
        remote_stops: remote_total,
        warnings: warning_total,
      })
    );
  }, [fetchedData]);

  const bars = [
    {key: 'warnings', label: 'Warning', color: 'yellow'},
    {key: 'hazards', label: 'Hazard', color: 'red'},
    {key: 'remote_stops', label: 'Remote Stop', color: 'purple'},
  ];

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      width="100%"
      overflow="hidden"
    >
      {fetchedErrors.map((error, idx) => (
        <Alert key={idx} severity="error" onClose={() => fetchData(params)}>
          {error}
        </Alert>
      ))}

      {fetchedData && !fetchedInProgress ? (
        <Box
          display="flex"
          height="100%"
          alignItems="center"
          justifyContent="center"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dataset} maxBarSize={MAX_BAR_SIZE}>
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip
                labelStyle={{
                  color: theme.palette.text.secondary,
                }}
                contentStyle={{
                  backgroundColor: theme.palette.background.default,
                }}
                cursor={false}
              />
              <Legend onClick={(item) => toggleKey(item.dataKey)} />

              {bars.map(({key, label, color}) => (
                <Bar
                  key={key}
                  dataKey={key}
                  name={label}
                  hide={hiddenKeys.includes(key)}
                  label={{
                    position: 'top',
                    fill: theme.palette.text.secondary,
                  }}
                  fill={color}
                  style={{
                    cursor: 'pointer',
                  }}
                  onClick={() => {}}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      ) : (
        <Box
          display="flex"
          height={50}
          alignItems="center"
          justifyContent="center"
        >
          {fetchedInProgress ? 'Loading...' : 'No data to display'}
        </Box>
      )}
    </Box>
  );
};

export default MachineInteractionReportBar;
